import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { handleDatePickerRawChange, dateFormatMaxLength } from "../../fields/datePickerInput";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  locationWiseAttendanceReportAPI,
  FetchLocationApi,
} from "../../actioncreators/actioncreators";
import {
  Search,
  Download,
  ChevronDown,
  ChevronUp,
  MapPin,
} from "lucide-react";
import "./dashboard.css";

function LocationWiseReport() {
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [locations, setLocations] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedLocations, setExpandedLocations] = useState({});
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);

  // Fetch locations on mount and select all by default
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const payload = {
          entity_type: "location",
          pagenumber: 1,
          pageSize: 200,
          parameter: [{ columnName: "status", condition: "=", value: "ACTIVE" }],
        };
        const response = await FetchLocationApi(payload);
        if (response?.data?.businessStatusCode === 2) {
          const locs = response.data?.viewListData || [];
          setLocations(locs);
          setSelectedLocationIds(locs.map((l) => l.location_id));
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };
    fetchLocations();
  }, []);

  const handleLocationToggle = (locationId) => {
    setSelectedLocationIds((prev) =>
      prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLocationIds.length === locations.length) {
      setSelectedLocationIds([]);
    } else {
      setSelectedLocationIds(locations.map((l) => l.location_id));
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleGenerateReport = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select both from and to dates");
      return;
    }

    setIsLoading(true);
    try {
      const requestBody = {
        locationIds: selectedLocationIds,
        fromDate: formatDate(fromDate),
        toDate: formatDate(toDate),
      };

      const response = await locationWiseAttendanceReportAPI(requestBody);
      // The API helper returns the full axios response, so the payload is under
      // response.data (businessStatusCode + locations live there). Reading it
      // at the top level was always undefined -> "Failed to generate report"
      // even on a successful 200 response.
      const result = response?.data ?? response;
      if (result?.businessStatusCode === 2) {
        setReportData(result);
        toast.success("Report generated successfully");
      } else {
        toast.error(result?.message || "Failed to generate report");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Error generating report");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLocationExpand = (locationId) => {
    setExpandedLocations((prev) => ({
      ...prev,
      [locationId]: !prev[locationId],
    }));
  };

  const handleDownloadPDF = () => {
    if (!reportData?.locations || reportData.locations.length === 0) {
      toast.error("No data to download");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    reportData.locations.forEach((location, locIdx) => {
      if (locIdx !== 0) doc.addPage();

      // Title
      doc.setFontSize(14);
      doc.setTextColor(34, 34, 34);
      doc.setFont("helvetica", "bold");
      doc.text("Location-wise Attendance Report", pageWidth / 2, 22, { align: "center" });

      // Section Divider
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.7);
      doc.line(14, 27, pageWidth - 14, 27);

      // Location Info
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Location:", 18, 38);
      doc.text("Period:", 18, 46);
      doc.text("Total Members:", 18, 54);
      doc.text("Total Worked Hours:", 18, 62);
      doc.text("Present Count:", 18, 70);
      doc.text("Absent Count:", 18, 78);
      doc.setFont("helvetica", "normal");
      doc.text(`${location.location_name || "-"}`, 65, 38);
      doc.text(`${formatDate(fromDate)} to ${formatDate(toDate)}`, 65, 46);
      doc.text(`${location.totalMembers ?? "-"}`, 65, 54);
      doc.text(`${location.totalWorkedHours || "-"}`, 65, 62);
      doc.text(`${location.presentCount ?? "-"}`, 65, 70);
      doc.text(`${location.absentCount ?? "-"}`, 65, 78);

      // Section Divider
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(14, 83, pageWidth - 14, 83);

      // Table Title
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(34, 34, 34);
      doc.text("Member Attendance Details", 18, 93);

      if (location.members && location.members.length > 0) {
        const memberRows = location.members.map((member, index) => [
          index + 1,
          member.full_name || "-",
          member.role_name || "-",
          member.presentDays ?? 0,
          member.absentDays ?? 0,
          member.totalWorkedHours || "0h 0m",
        ]);

        autoTable(doc, {
          startY: 98,
          head: [["#", "Name", "Role", "Present Days", "Absent Days", "Total Worked Hours"]],
          body: memberRows,
          theme: "striped",
          headStyles: {
            fillColor: [44, 62, 80],
            textColor: [255, 255, 255],
            fontSize: 8,
            fontStyle: "bold",
            halign: "center",
            valign: "middle",
          },
          bodyStyles: {
            fontSize: 7,
            textColor: [34, 34, 34],
            halign: "center",
            valign: "middle",
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
          },
          columnStyles: {
            0: { cellWidth: 12 },
            1: { cellWidth: 45 },
            2: { cellWidth: 30 },
            3: { cellWidth: 28 },
            4: { cellWidth: 28 },
            5: { cellWidth: 35 },
          },
          margin: { left: 14, right: 14 },
          styles: {
            cellPadding: 2.5,
          },
          didDrawPage: () => {
            doc.setDrawColor(220, 220, 220);
            doc.setLineWidth(0.3);
            doc.line(14, doc.internal.pageSize.getHeight() - 18, pageWidth - 14, doc.internal.pageSize.getHeight() - 18);
          },
        });
      } else {
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(150, 150, 150);
        doc.text("No members assigned to this location", 18, 103);
      }
    });

    // Footer for all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    doc.save(
      `Location_Report_${formatDate(fromDate)}_to_${formatDate(toDate)}.pdf`
    );
    toast.success("PDF downloaded successfully");
  };

  return (
    <div className="row bg-white rounded shadow pt-3 px-3">
      <div className="col-12 col-md-12 d-flex justify-content-between p-0">
        <h1 className="fw-bold">Location-wise Attendance Report</h1>
      </div>

      <div className="row p-0 bg-white my-2 pb-1 border rounded d-flex m-0">
        {/* From Date */}
        <div className="col-12 col-md-2 mb-2 pt-2">
          <label className="form-label">From Date</label>
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            onChangeRaw={handleDatePickerRawChange("dd/MM/yyyy")}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            maxDate={toDate}
            maxLength={dateFormatMaxLength("dd/MM/yyyy")}
            withPortal
          />
        </div>

        {/* To Date */}
        <div className="col-12 col-md-2 mb-2 pt-2">
          <label className="form-label">To Date</label>
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            onChangeRaw={handleDatePickerRawChange("dd/MM/yyyy")}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            minDate={fromDate}
            maxDate={new Date()}
            maxLength={dateFormatMaxLength("dd/MM/yyyy")}
            withPortal
          />
        </div>

        {/* Locations Dropdown */}
        <div className="col-12 col-md-5 mb-2 pt-2" style={{ position: "relative" }}>
          <label className="form-label">Select Location(s)</label>
          <div
            className="form-control d-flex align-items-center justify-content-between"
            style={{ cursor: "pointer", minHeight: "38px" }}
            onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
          >
            <span style={{ color: selectedLocationIds.length > 0 ? "#333" : "#999", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {selectedLocationIds.length === 0
                ? "Select locations..."
                : selectedLocationIds.length === locations.length
                ? `All Locations (${locations.length})`
                : `${selectedLocationIds.length} location(s) selected`}
            </span>
            <ChevronDown style={{ width: 16, height: 16, color: "#666", flexShrink: 0, transform: isLocationDropdownOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </div>
          {isLocationDropdownOpen && (
            <div
              className="border rounded bg-white shadow"
              style={{ position: "absolute", top: "100%", left: "12px", right: "12px", zIndex: 1050, maxHeight: "220px", overflowY: "auto", marginTop: "2px" }}
            >
              <div className="form-check px-3 py-2 border-bottom" style={{ backgroundColor: "#f8f9fa" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="selectAll"
                  checked={selectedLocationIds.length === locations.length && locations.length > 0}
                  onChange={handleSelectAll}
                />
                <label className="form-check-label fw-bold" htmlFor="selectAll">
                  {selectedLocationIds.length === locations.length ? "Deselect All" : "Select All"}
                </label>
              </div>
              {locations.map((loc) => (
                <div className="form-check px-3 py-1" key={loc.location_id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`loc-${loc.location_id}`}
                    checked={selectedLocationIds.includes(loc.location_id)}
                    onChange={() => handleLocationToggle(loc.location_id)}
                  />
                  <label className="form-check-label" htmlFor={`loc-${loc.location_id}`}>
                    {loc.location_name}
                  </label>
                </div>
              ))}
              {locations.length === 0 && (
                <div className="px-3 py-2 text-muted">No locations found</div>
              )}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="col-12 col-md-3 mb-2 d-flex align-items-end gap-2">
          <button
            className="btn shadow-sm w-100"
            onClick={handleGenerateReport}
            disabled={isLoading || selectedLocationIds.length === 0}
            style={{
              backgroundColor: selectedLocationIds.length > 0 ? '#ff7f0e' : '#ffe2d0',
              color: selectedLocationIds.length > 0 ? '#fff' : '#e0bfae',
              border: 'none',
              borderRadius: '32px',
              fontWeight: 'bold',
              fontSize: '16px',
              padding: '12px 0',
              cursor: selectedLocationIds.length > 0 ? 'pointer' : 'not-allowed',
              opacity: selectedLocationIds.length > 0 ? 1 : 0.7,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'background 0.2s',
            }}
          >
            <Search style={{ width: 16, height: 16 }} />
            {isLoading ? "Generating..." : "Generate Report"}
          </button>
        </div>
      </div>

      {/* Download PDF button */}
      {reportData && (
        <div className="row p-0 m-0 mb-2">
          <div className="col-12 d-flex justify-content-end p-0">
            <button
              className="btn btn-success btn-sm d-flex align-items-center gap-1"
              onClick={handleDownloadPDF}
            >
              <Download style={{ width: 14, height: 14 }} />
              Download PDF
            </button>
          </div>
        </div>
      )}

      {/* Report Results */}
      {reportData?.locations && reportData.locations.length > 0 && (
        <div>
          {reportData.locations.map((location) => (
            <div
              key={location.location_id}
              className="bg-white shadow-sm mb-2"
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {/* Location Header */}
              <div
                className="d-flex justify-content-between align-items-center p-3"
                style={{
                  backgroundColor: "#f8f9fa",
                  cursor: "pointer",
                  borderBottom: expandedLocations[location.location_id]
                    ? "1px solid #e0e0e0"
                    : "none",
                }}
                onClick={() => toggleLocationExpand(location.location_id)}
              >
                <div className="d-flex align-items-center gap-2">
                  <MapPin
                    style={{ width: 16, height: 16, color: "#007bff" }}
                  />
                  <strong style={{ fontSize: "14px" }}>
                    {location.location_name}
                  </strong>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <span
                    className="badge bg-primary"
                    style={{ fontSize: "11px" }}
                  >
                    {location.totalMembers} Members
                  </span>
                  <span
                    className="badge bg-success"
                    style={{ fontSize: "11px" }}
                  >
                    {location.totalWorkedHours}
                  </span>
                  {expandedLocations[location.location_id] ? (
                    <ChevronUp style={{ width: 16, height: 16 }} />
                  ) : (
                    <ChevronDown style={{ width: 16, height: 16 }} />
                  )}
                </div>
              </div>

              {/* Expanded Member Table */}
              {expandedLocations[location.location_id] && (
                <div className="p-3">
                  {location.members && location.members.length > 0 ? (
                    <table
                      className="table table-sm table-hover mb-0"
                      style={{ fontSize: "13px" }}
                    >
                      <thead>
                        <tr style={{ backgroundColor: "#f1f3f5" }}>
                          <th>#</th>
                          <th>Name</th>
                          <th>Role</th>
                          <th>Present Days</th>
                          <th>Absent Days</th>
                          <th>Total Worked Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {location.members.map((member, index) => (
                          <tr key={member.user_id}>
                            <td>{index + 1}</td>
                            <td>{member.full_name || "-"}</td>
                            <td>{member.role_name || "-"}</td>
                            <td>
                              <span className="badge bg-success">
                                {member.presentDays}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-danger">
                                {member.absentDays}
                              </span>
                            </td>
                            <td>{member.totalWorkedHours}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                      No members assigned to this location
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!reportData && (
        <div className="text-center p-5" style={{ color: "#6c757d", fontSize: "14px" }}>
          Kindly generate the report to view it here.
        </div>
      )}
      {reportData && reportData.locations?.length === 0 && (
        <div className="text-center p-5" style={{ color: "#6c757d", fontSize: "14px" }}>
          No data found for the selected locations and date range.
        </div>
      )}
    </div>
  );
}

export default LocationWiseReport;
